import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

import { CimaCore, Cloudinary, Util, XmStore, XmStoreUtil } from 'core/services';
import { StoreAction } from 'store/actions';

import { LevelMoveEligibility } from 'store/level-move/models';
import { RequestStatus } from 'store/request-status/models';
import { CmsLevelMoveInProgress } from 'store/cms/models';
import { CONFIG_TOKEN } from 'core/constants';

import { PaymentState, ProgressStatus, REQUEST_INTERVAL, REQUEST_TIMEOUT } from 'my-account/constants';
import { StateService } from '@uirouter/core';
import { Account } from 'store/user/models';
import { IXMOptions } from 'core/interfaces';

/**
 * Represents the component for handling level move in progress.
 */
@Component({
    selector: 'xm-level-move-in-progress',
    templateUrl: './in-progress.html',
    styleUrls: ['./in-progress.scss'],
    standalone: false
})
export class XmLevelMoveInProgress implements OnInit, OnDestroy {
    public pageData: CmsLevelMoveInProgress;
    public icon: MediaImageOptions;
    public isBillPreviewEnabled: boolean = false;
    public isBusinessUser: boolean = false;

    private requestApiInterval: NodeJS.Timer;
    private switchTimeout: NodeJS.Timer;
    private xmStore: XmStore;
    private state: StateService;
    private subscriptions: Subscription[] = [];
    private cimaCore: CimaCore;
    private config: IXMOptions;

    constructor(cimaCore: CimaCore, xmStore: XmStore, state: StateService, @Inject(CONFIG_TOKEN) config: IXMOptions) {
        Object.assign(this, { cimaCore, xmStore, state, config });
    }

    public ngOnInit(): void {
        this.isBusinessUser = this.cimaCore.isBusinessUser;
        this.isBillPreviewEnabled = (this.config.ENABLE_BILL_PREVIEW && !this.isBusinessUser) || (this.config.ENABLE_CBM_BILL_PREVIEW && this.isBusinessUser);
        // Subscribe to XmStoreUtil for page data updates
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsLevelMoveInProgress>(CmsLevelMoveInProgress), (result: CmsLevelMoveInProgress) => {
            this.pageData = result;
            this.icon = {
                mobile: Cloudinary.generateMediaFromCms(this.pageData.icon)
            };
        }));

        // Subscribe to XmStoreUtil for level move eligibility updates
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<LevelMoveEligibility>(LevelMoveEligibility), (levelMoveEligibility: LevelMoveEligibility) => {
            this.levelMove(levelMoveEligibility.toLevel);
        }));
    }

    
    /**
     * Moves the level to the specified level.
     * 
     * This method performs a level move and handles the response. It first sends a POST request to the server
     * to initiate the level move with the specified `toLevel`. If the request is successful, it starts polling
     * the server every 5 seconds to check the status of the level move. If the status is `SUCCESS` or `FAIL`,
     * the level move is considered complete and the `completeLevelMove` method is called with the corresponding status.
     * If the status is `IN_PROGRESS`, a timeout is set to complete the level move after a certain duration defined by `REQUEST_TIMEOUT`.
     * If the request to the server fails, a timeout is set to complete the level move after `REQUEST_TIMEOUT` duration.
     * 
     * @param toLevel - The level to move to.
     * @returns void
     */
    public levelMove(toLevel: string): void {
        const storeAction: StoreAction = this.isBillPreviewEnabled ? StoreAction.POST_SSM_LEVEL_MOVE : StoreAction.POST_LEVEL_MOVE;
        this.xmStore.persist<RequestStatus>(storeAction, { toLevel, ...(this.state.params?.planOverrides && {planOverrides: this.state.params?.planOverrides}), ...(this.isBillPreviewEnabled && {trialFeature: 'TRIAL_EBP'})})
            .then((levelMoveStatus: RequestStatus) => {
                // every 5 seconds, call request status api unless got back success or failure status
                this.requestApiInterval = setInterval(() => {
                    this.queryStatus(levelMoveStatus.requestId.key).then((requestStatus: RequestStatus) => {
                        if (requestStatus.status === ProgressStatus.SUCCESS || requestStatus.status === ProgressStatus.FAIL) {
                            // if status is SUCCESS or FAIL, complete Level Switch right away
                            this.completeLevelMove(requestStatus.status);
                        } else if (!this.switchTimeout && requestStatus.status === ProgressStatus.IN_PROGRESS) {
                            // set timeout only once when got status IN_PROGRESS
                            this.switchTimeout = setTimeout(() => {
                                this.completeLevelMove(ProgressStatus.IN_PROGRESS);
                            }, REQUEST_TIMEOUT);
                        }
                    }).catch(() => {
                        // set timeout if request api is failing
                        this.switchTimeout = setTimeout(() => {
                            this.completeLevelMove(ProgressStatus.IN_PROGRESS);
                        }, REQUEST_TIMEOUT);
                    });
                }, REQUEST_INTERVAL);
            }).catch(() => {
                // if Level switch api fails then display failure dialog
                this.completeLevelMove(ProgressStatus.FAIL);
            });
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    /**
     * Completes the level move based on the level move status.
     * @param levelMoveStatus The status of the level move.
     */
    private completeLevelMove(levelMoveStatus: string): void {
        clearInterval(this.requestApiInterval);
        clearTimeout(this.switchTimeout);
        this.clearAndRefetchApis();

        switch (levelMoveStatus) {
            case ProgressStatus.IN_PROGRESS:
                this.state.go('account.settings.level-move.pending');
                break;
            case ProgressStatus.SUCCESS:
                this.state.go('account.settings.level-move.success');
                break;
            case ProgressStatus.FAIL:
                this.state.go('account.settings.level-move.failure');
                break;
        }
    }

    /**
     * Clears and refetches the relevant APIs.
     */
    private clearAndRefetchApis(): void {
        // fetch account info
        this.xmStore.fetchPromise<Account>(StoreAction.GET_ACCOUNT_INFO);

        // clear other relevant models from store
        const clearApisList: StoreDeleteAction[] = [
            { action: StoreAction.CLEAR_HERO_BANNER_INFO },
            { action: StoreAction.CLEAR_LINE_BANNER_INFO },
            { action: StoreAction.CLEAR_ACCOUNT_SUMMARY },
            { action: StoreAction.CLEAR_STATEMENT, filters: { paymentState: PaymentState.ESTIMATED } }
        ];

        this.xmStore.clearApis(clearApisList);
    }

    /**
     * Queries the status of a request.
     * @param requestId The ID of the request.
     * @returns A promise that resolves to the request status.
     */
    private queryStatus(requestId: string): Promise<RequestStatus> {
        return this.xmStore.queryPromise<RequestStatus>(StoreAction.GET_REQUEST_STATUS, { requestId });
    }
}
